import React from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
} from "@mui/material";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApi } from "../../utils/api";
import { useAuth } from "../../utils/auth";
import { useUserInfo } from "../../utils/user";
import ImpersonateUserTab from "./ImpersonateUserTab";
import MarketingInstagramTab from "./MarketingInstagramTab";

const PLATFORM_OWNER_USER_ID = import.meta.env.VITE_PLATFORM_OWNER_USER_ID;

const TAB_SLUGS = ["impersonate", "instagram"];

function Admin() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const slug = searchParams.get("tab");
  const tabIndex = TAB_SLUGS.includes(slug) ? TAB_SLUGS.indexOf(slug) : 0;

  const { listUsers, impersonateUser: apiImpersonate } = useApi();
  const { signInWithCustomTokenAuth } = useAuth();
  const { id: currentUserId, loading: userInfoLoading } = useUserInfo();

  const isPlatformUser = currentUserId === PLATFORM_OWNER_USER_ID;

  const handleTabChange = (_, newIndex) => {
    setSearchParams({ tab: TAB_SLUGS[newIndex] }, { replace: true });
  };

  if (userInfoLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isPlatformUser) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Typography variant="h6" gutterBottom>
          Admin
        </Typography>
        <Typography color="text.secondary">
          You are not authorized to use this page.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Typography
          variant="h5"
          gutterBottom
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <AdminPanelSettingsIcon aria-hidden />
          Admin
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Platform tools — impersonation and marketing Instagram connection.
        </Typography>

        <Tabs value={tabIndex} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label="Impersonate user" id="admin-tab-0" aria-controls="admin-tabpanel-0" />
          <Tab
            label="Home page Instagram"
            id="admin-tab-1"
            aria-controls="admin-tabpanel-1"
          />
        </Tabs>

        <Box
          role="tabpanel"
          id="admin-tabpanel-impersonate"
          hidden={tabIndex !== 0}
          sx={{ mt: 1 }}
        >
          {tabIndex === 0 && (
            <ImpersonateUserTab
              listUsers={listUsers}
              apiImpersonate={apiImpersonate}
              signInWithCustomTokenAuth={signInWithCustomTokenAuth}
              navigate={navigate}
              platformOwnerUserId={PLATFORM_OWNER_USER_ID}
              currentUserId={currentUserId}
            />
          )}
        </Box>

        <Box
          role="tabpanel"
          id="admin-tabpanel-instagram"
          hidden={tabIndex !== 1}
          sx={{ mt: 1 }}
        >
          {tabIndex === 1 && (
            <MarketingInstagramTab userId={currentUserId} />
          )}
        </Box>
      </Paper>
    </Container>
  );
}

export default Admin;
